import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport for the staff directory (PTV2-1567) — `GET /api/v1/staff/{uuid}` and
 * `GET /api/v1/staff/search`. Unlike `/users`, these are readable by ANY authenticated
 * tenant user and return a curated, names-only view: no email, login name, status or
 * roles. Used to turn a user guid on a record (task assignee, "completed by", …) into
 * a display label.
 *
 * Hand-written to match `components.schemas.Staff` in the ai-hub contract: `schema.ts`
 * can't be regenerated until the contract's duplicate `PartialDate` key is fixed.
 */

/** Live BE shape for `GET /staff/{uuid}`. Null fields are omitted, not sent as `null`. */
export interface StaffDto {
  readonly uuid: string;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly title?: string;
  /**
   * Server-resolved label: first/last name, else full name, else email. Omitted when
   * none is set; render "Unknown user" then, never the uuid.
   */
  readonly display_name?: string;
}

/** Live BE shape for `GET /staff/search` (the standard Spring `Page` envelope). */
export type PageStaffDto = PageEnvelope<StaffDto>;

export const staffEndpoints = {
  /** GET /staff/{uuid} — resolve a user guid to a display label. 404 for an unknown guid. */
  getStaffMember: (uuid: string, options?: { signal?: AbortSignal }): Promise<StaffDto> =>
    http.get<StaffDto>(`/staff/${encodeURIComponent(uuid)}`, options),

  /**
   * GET /staff/search?q=…&page=&size= — phase 1 matches login name, full name or email
   * and is not yet filtered to clinical, active users, so it has no caller yet.
   */
  searchStaff: (
    params: { q: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageStaffDto> =>
    http.get<PageStaffDto>('/staff/search', {
      ...options,
      query: {
        q: params.q,
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    }),
};
